<script lang="ts">
  import { BookingForm, BookingSent } from '.';
  import { Heading, Paragraph } from '../../components';
  import type {
    GlobalProperties,
    UseFormattedProps,
  } from 'customer-components/lib/types';

  export let properties: UseFormattedProps & {
    heading: string;
    text: string;
    kicker: {
      value: string;
    };
    image: {
      value: string;
    };
  };
  export let generalSettings: GlobalProperties['generalSettings'];
  export let isHighlight = false;
  export let hasBackgroundImage = false;
  export let isSplit = false;
  export let wrapSentMessageInCard = false;

  let sent = false;

  $: booking =
    generalSettings?.callToAction?.value === 'book-external' ||
    generalSettings?.callToAction?.value === 'book-internal';
  $: externalBooking =
    booking && generalSettings?.callToAction?.value === 'book-external';

  $: defaultHeading = booking
    ? 'Book An Appointment'
    : generalSettings?.callToAction?.value;
  $: defaultKicker = booking
    ? 'Ready to schedule your appointment?'
    : 'Contact us now';
  $: defaultSubheading = externalBooking
    ? " Start by filling out the form below. Once completed, you'll be redirected to an external link to finalize your booking. Let's get started!"
    : 'We are ready to help you with your needs';
</script>

<section
  id="form"
  class="contact"
  class:is-highlight={isHighlight}
  class:has-background-image={hasBackgroundImage}
  class:is-split={isSplit}
  style="--bg-image: url({properties?.image?.value})"
>
  <div class="contact-inner">
    <hgroup class="contact-title">
      <p class="pretitle">{properties?.kicker?.value || defaultKicker}</p>
      <Heading type="h2" css="color: var(--text-color)">
        {properties.heading || defaultHeading}
      </Heading>
      <Paragraph css="color: var(--text-color)">
        {properties?.text || defaultSubheading}
      </Paragraph>
    </hgroup>
    <div class="contact-form">
      <BookingForm
        {properties}
        {generalSettings}
        {booking}
        {externalBooking}
        hasCalendar={booking && !externalBooking}
        bind:sent
      />
    </div>
  </div>
</section>

<style lang="scss">
  .contact {
    --text-color: var(--vlt-lp-text);
    display: flex;
    justify-content: center;
    padding: 7.5rem 0;
    background-color: var(--vlt-lp-background);

    &.is-highlight {
      --text-color: var(--vlt-lp-secondary-text);
      background-color: var(--vlt-lp-secondary);
    }

    &.has-background-image {
      --text-color: var(--color-neutralWhite);
      background: linear-gradient(
          rgba(17, 17, 17, 0.64),
          rgba(17, 17, 17, 0.64)
        ),
        var(--bg-image);
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
    }

    &-inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      max-width: 53rem;
      padding: 0 2rem;
      text-align: center;
      box-sizing: border-box;
    }

    &-title {
      margin-bottom: var(--spacing-large);

      .pretitle {
        margin-bottom: var(--spacing-quarck);
        color: var(--vlt-lp-accent);
        font-size: 14px;
        font-weight: 600;
        line-height: 24px;
        letter-spacing: 1.4px;
        text-transform: uppercase;
      }

      :global(.heading) {
        margin-bottom: var(--spacing-xsmall);
      }
    }

    &-form {
      width: 100%;
      max-width: 46rem;
    }

    &.is-split {
      padding: 0;

      .contact {
        &-inner {
          flex-direction: row;
          max-width: unset;
          padding: 0;
        }

        &-title {
          --text-color: var(--color-neutralWhite);
          max-width: 48rem;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 0 130px;
          margin-bottom: 0;
          text-align: left;
          background: linear-gradient(
              90deg,
              rgba(0, 0, 0, 1),
              rgba(0, 0, 0, 0.5)
            ),
            var(--bg-image);
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;
        }

        &-form {
          width: 100%;
          max-width: 39rem;
          margin: 7.5rem auto;
        }
      }
    }
  }

  @media screen and (max-width: 1440px) {
    .contact.is-split .contact-title {
      max-width: 40rem;
      padding: 0 65px;
    }
  }

  @media screen and (max-width: 1024px) {
    .contact {
      padding: var(--spacing-middle-xl) 0;

      &.has-background-image {
        padding: 80px 0;
      }

      &-inner {
        padding: 0 var(--spacing-mini);
      }

      &.is-split {
        .contact {
          &-inner {
            flex-direction: column;
          }

          &-title {
            max-width: unset;
            padding: 80px var(--spacing-mini);
            text-align: center;
          }

          &-form {
            padding: var(--spacing-large) var(--spacing-mini);
            margin: 0;
          }
        }
      }
    }
  }
</style>
